import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../lib/auth/AuthProvider';
import {
  attestRecovery,
  getPendingRecoveries,
  type PendingRecoveryRow,
} from '../lib/advisorApi';
import { formatDate } from '../lib/format';
import './advisor.css';

/**
 * "Getting back in" — clients who have lost their phone.
 *
 * This screen exists because attestation is the fast door and it had no
 * handle. A client with neither their old device nor their Recovery Kit waits
 * 72 hours; an advisor who verifies them face to face can end that wait today.
 *
 * The screen is built to make the advisor stop and think, because their
 * signature is the one thing that bypasses the whole control:
 *
 *   - it shows where the request came from, so an obviously wrong IP or device
 *     is visible before they act;
 *   - it will not submit without a note, because "how do you know it is them"
 *     should have an answer someone can read back later;
 *   - the confirm text names the consequence rather than saying "confirm".
 *
 * An advisor only ever sees their own clients — the list and the write share
 * the same predicate, so the screen can never offer a button the server would
 * refuse.
 */
export function RecoveriesScreen() {
  const { api } = useAuth();
  const [rows, setRows] = useState<PendingRecoveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [method, setMethod] = useState<'video_call' | 'in_person'>('video_call');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getPendingRecoveries(api);
      setRows(res.recoveries);
    } catch {
      setError('We could not load recovery requests.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function vouch(id: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await attestRecovery(api, id, method, note.trim());
      setOpenId(null);
      setNote('');
      await load();
    } catch {
      setError('That did not go through. The request may already have been stopped.');
    } finally {
      setBusy(false);
    }
  }

  if (rows === null && error === null) {
    return <div className="la-rise"><p className="la-label">Loading…</p></div>;
  }

  return (
    <div className="la-rise">
      <p className="la-label">Getting back in</p>
      <h1 className="page__title">Lost devices</h1>
      <p className="page__sub">
        Clients who have asked to get back into their vault after losing their phone. They wait
        three days by default. If you have verified who they are — on a video call or in person —
        you can end that wait now.
      </p>

      {error !== null && <p className="auth__alert" role="alert">{error}</p>}

      {rows !== null && rows.length === 0 && (
        <p className="la-card page__empty">Nobody is locked out. Nothing to do here.</p>
      )}

      {rows?.map((r) => {
        const waitingUntil = new Date(r.coolingOffEndsAt);
        const ready = r.status === 'ready' || waitingUntil.getTime() <= Date.now();
        return (
          <div key={r.id} className="la-card formCard">
            <div className="rowActions recovery__head">
              <div>
                <div className="table__name">{r.ownerName}</div>
                <div className="page__sub recovery__meta">{r.ownerEmail}</div>
              </div>
              <div className="page__sub recovery__meta">
                {ready ? 'Can set up a new device now' : `Waiting until ${formatDate(r.coolingOffEndsAt)}`}
              </div>
            </div>

            {/* Shown so an obviously wrong origin is visible BEFORE vouching. */}
            <div className="note">
              Asked {formatDate(r.requestedAt)}
              {r.requestIp !== null && <> · from {r.requestIp}</>}
              {r.requestUserAgent !== null && <> · {r.requestUserAgent.slice(0, 60)}</>}
            </div>

            {!ready && openId !== r.id && (
              <button type="button" className="la-btn-secondary" onClick={() => { setOpenId(r.id); setNote(''); }}>
                I have verified them
              </button>
            )}

            {openId === r.id && (
              <div className="recovery__vouch">
                <p className="privacyNote recovery__warn">
                  This ends the three-day wait for {r.ownerName} immediately. Whoever completes the
                  recovery will have their access to the vault, including messages that have not
                  been delivered. Only do this if you are certain you have spoken to them.
                </p>
                <label className="field-wrap">
                  <span className="auth__label">How did you verify them?</span>
                  <select
                    className="field field--wide"
                    value={method}
                    onChange={(e) => setMethod(e.target.value === 'in_person' ? 'in_person' : 'video_call')}
                  >
                    <option value="video_call">A video call</option>
                    <option value="in_person">In person</option>
                  </select>
                </label>
                <label className="field-wrap">
                  <span className="auth__label">What happened? (recorded against your name)</span>
                  <textarea
                    className="field field--wide"
                    rows={3}
                    value={note}
                    placeholder="e.g. Video call 17 Aug, recognised her, she confirmed her daughter's name and the year she joined."
                    onChange={(e) => setNote(e.target.value)}
                  />
                </label>
                <div className="rowActions">
                  <button
                    type="button"
                    className="la-btn-primary"
                    disabled={busy || note.trim().length < 10}
                    onClick={() => void vouch(r.id)}
                  >
                    {busy ? 'Recording…' : `Vouch for ${r.ownerName}`}
                  </button>
                  <button type="button" className="la-btn-secondary" onClick={() => setOpenId(null)} disabled={busy}>
                    Cancel
                  </button>
                </div>
                {note.trim().length < 10 && (
                  <p className="page__sub recovery__meta">Write a sentence about how you know it is them.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
